import React, { useState, useEffect } from 'react';
import { Users, Mail, Building2, Trash2, Loader2, Download, ToggleLeft, ToggleRight, Search, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import AdminLogin from './AdminLogin';
import AdminHeader from './AdminHeader';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { apiUrl } from '../utils/config';
import logger from '../utils/logger';

const SubscribersAdmin = ({ onBackToHome }) => {
  const { isAuth, isCheckingAuth, handleLogin, handleLogout } = useAdminAuth();
  const [subscribers, setSubscribers] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, today: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewsletter, setShowNewsletter] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [newsletter, setNewsletter] = useState({ subject: '', body: '', target: 'newsletter' });

  useEffect(() => {
    if (isAuth) {
      fetchSubscribers();
      fetchStats();
    }
  }, [isAuth]);

  const fetchSubscribers = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(apiUrl('subscribers/admin'));
      if (response.ok) setSubscribers(await response.json());
    } catch (error) {
      logger.error('Error fetching subscribers:', error);
      toast.error('Failed to load subscribers');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(apiUrl('subscribers/admin/stats'));
      if (response.ok) setStats(await response.json());
    } catch (error) {
      logger.error('Error fetching stats:', error);
    }
  };

  const handleToggle = async (id, currentActive) => {
    try {
      const response = await fetch(apiUrl(`subscribers/${id}/toggle`), { method: 'PUT' });
      if (response.ok) {
        setSubscribers(prev => prev.map(s => s.id === id ? { ...s, active: !currentActive } : s));
        fetchStats();
        toast.success(currentActive ? 'Subscriber deactivated' : 'Subscriber reactivated');
      }
    } catch (error) {
      logger.error('Toggle error:', error);
      toast.error('Failed to update subscriber');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to permanently delete this subscriber?')) return;
    try {
      const response = await fetch(apiUrl(`subscribers/${id}`), { method: 'DELETE' });
      if (response.ok) {
        setSubscribers(prev => prev.filter(s => s.id !== id));
        fetchStats();
        toast.success('Subscriber deleted');
      }
    } catch (error) {
      logger.error('Delete error:', error);
      toast.error('Failed to delete subscriber');
    }
  };

  const handleExport = async () => {
    try {
      const response = await fetch(apiUrl('subscribers/export'), { method: 'POST' });
      if (response.ok) {
        const data = await response.json();
        const csv = [
          'Name,Email,Company,Role,Type,Subscribed At',
          ...data.subscribers.map(s => `"${s.name}","${s.email}","${s.company || ''}","${s.role || ''}","${s.subscriptionType || 'both'}","${s.subscribedAt}"`)
        ].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `subscribers_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`Exported ${data.count} subscribers`);
      }
    } catch (error) {
      logger.error('Export error:', error);
      toast.error('Failed to export subscribers');
    }
  };

  const handleSendNewsletter = async () => {
    if (!newsletter.subject.trim() || !newsletter.body.trim()) {
      toast.error('Subject and content are required');
      return;
    }
    setIsSending(true);
    try {
      const response = await fetch(apiUrl('subscribers/send-newsletter'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: newsletter.subject,
          body_html: newsletter.body,
          target: newsletter.target,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Newsletter sent to ${data.sent} subscribers`);
        setShowNewsletter(false);
        setNewsletter({ subject: '', body: '', target: 'newsletter' });
      } else {
        toast.error('Failed to send newsletter');
      }
    } catch (error) {
      logger.error('Newsletter error:', error);
      toast.error('Failed to send newsletter');
    } finally {
      setIsSending(false);
    }
  };

  if (isCheckingAuth) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  }

  if (!isAuth) return <AdminLogin onLogin={handleLogin} title="Subscribers Admin" />;

  const filtered = subscribers.filter(s =>
    s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.company?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="subscribers-admin">
      <AdminHeader
        title="Subscribers"
        onBackToHome={onBackToHome}
        onLogout={handleLogout}
      />

      <div className="container mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg"><Users className="w-5 h-5 text-blue-600" /></div>
              <div><div className="text-2xl font-bold text-gray-900">{stats.total}</div><div className="text-sm text-gray-500">Total</div></div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg"><Mail className="w-5 h-5 text-purple-600" /></div>
              <div><div className="text-2xl font-bold text-purple-600">{stats.newsletter || 0}</div><div className="text-sm text-gray-500">Newsletter</div></div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg"><Building2 className="w-5 h-5 text-blue-600" /></div>
              <div><div className="text-2xl font-bold text-blue-600">{stats.services || 0}</div><div className="text-sm text-gray-500">Services</div></div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg"><Mail className="w-5 h-5 text-emerald-600" /></div>
              <div><div className="text-2xl font-bold text-emerald-600">{stats.active}</div><div className="text-sm text-gray-500">Active</div></div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg"><Users className="w-5 h-5 text-amber-600" /></div>
              <div><div className="text-2xl font-bold text-amber-600">{stats.today}</div><div className="text-sm text-gray-500">Today</div></div>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by name, email, or company..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="subscriber-search"
            />
          </div>
          <Button onClick={handleExport} variant="outline" className="gap-2" data-testid="export-btn">
            <Download className="w-4 h-4" /> Export CSV
          </Button>
          <Button onClick={() => setShowNewsletter(true)} className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="send-newsletter-btn">
            <Send className="w-4 h-4" /> Send Newsletter
          </Button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">Subscriber</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">Company / Role</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">Type</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">Date</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">Status</th>
                <th className="text-right px-6 py-4 text-sm font-semibold text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr><td colSpan="6" className="px-6 py-12 text-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" /></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                  <Mail className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p>{searchQuery ? 'No subscribers match your search.' : 'No subscribers yet.'}</p>
                </td></tr>
              ) : (
                filtered.map((sub) => (
                  <tr key={sub.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-900">{sub.name}</p>
                      <p className="text-sm text-gray-500">{sub.email}</p>
                    </td>
                    <td className="px-6 py-4">
                      {sub.company && <div className="flex items-center gap-1 text-sm text-gray-700"><Building2 className="w-3 h-3" />{sub.company}</div>}
                      {sub.role && <p className="text-sm text-gray-500">{sub.role}</p>}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                        sub.subscriptionType === 'newsletter' ? 'bg-purple-100 text-purple-700' :
                        sub.subscriptionType === 'services' ? 'bg-blue-100 text-blue-700' :
                        'bg-indigo-100 text-indigo-700'
                      }`}>
                        {sub.subscriptionType === 'newsletter' ? 'Newsletter' : sub.subscriptionType === 'services' ? 'Services' : 'Both'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(sub.subscribedAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${sub.active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                        {sub.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => handleToggle(sub.id, sub.active)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500"
                          title={sub.active ? 'Deactivate' : 'Reactivate'}>
                          {sub.active ? <ToggleRight className="w-5 h-5 text-emerald-600" /> : <ToggleLeft className="w-5 h-5 text-gray-400" />}
                        </button>
                        <button onClick={() => handleDelete(sub.id)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500 hover:text-red-600" title="Delete">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Send Newsletter Modal */}
      {showNewsletter && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="newsletter-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Send Newsletter</h2>
              <button onClick={() => setShowNewsletter(false)} className="text-gray-400 hover:text-gray-600">
                <Mail className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Target Audience */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Send to</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'newsletter', label: 'Newsletter', count: stats.newsletter || 0 },
                    { value: 'services', label: 'Services', count: stats.services || 0 },
                    { value: 'all', label: 'Everyone', count: stats.active },
                  ].map(opt => (
                    <button key={opt.value} type="button"
                      onClick={() => setNewsletter(prev => ({ ...prev, target: opt.value }))}
                      className={`px-3 py-2 text-sm rounded-lg border-2 font-medium transition-all ${
                        newsletter.target === opt.value ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}>
                      {opt.label} ({opt.count})
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                <Input
                  value={newsletter.subject}
                  onChange={(e) => setNewsletter(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="e.g., New Blog Post: The Future of Executive Coaching"
                  data-testid="newsletter-subject"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Content (HTML) *</label>
                <Textarea
                  value={newsletter.body}
                  onChange={(e) => setNewsletter(prev => ({ ...prev, body: e.target.value }))}
                  placeholder="Write your newsletter content here... HTML is supported."
                  rows={8}
                  data-testid="newsletter-body"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowNewsletter(false)} className="flex-1">Cancel</Button>
                <Button onClick={handleSendNewsletter} disabled={isSending || !newsletter.subject || !newsletter.body}
                  className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="newsletter-send-btn">
                  {isSending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</> : <><Send className="w-4 h-4 mr-2" />Send</>}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscribersAdmin;
